/**
 * Recalculer les prix des gammes d'une marque : la revue, côté écran (lot P2, registre B26).
 *
 * Fonctions PURES, sans React : quelles gammes sont cochées d'office, ce qu'on dit de ce qui MANQUE pour que les prix
 * se calculent, ce qui part vers le serveur, ce qu'on annonce après la création des brouillons. Règles posées avec
 * Benjamin (2026-10-15) : **toute la marque d'un coup** — une seule revue, un brouillon par gamme concernée, puis
 * « Publier les N brouillons » ; **rien n'est jamais deviné** — une famille sans condition n'a pas de prix de vente,
 * et l'écran dit QUOI saisir pour qu'elle en ait un.
 *
 * Le client n'envoie QUE les gammes et la date d'effet : le serveur recalcule tout et reste l'autorité.
 */
import type {
  BilanGammeRecalculPrix,
  BilanRecalculPrix,
  CodeRefusGamme,
  GammeARecalculer,
  ManqueDeConditionPrix,
  ResumeGammeRecalcul,
} from '../api/client';
import { accordFr } from './gammes-tarif';
import { erreurDateDEffet, lignesFr } from './tarifs';

/** Les gammes cochées d'office : celles dont au moins un prix changerait. Les autres n'ont rien à gagner. */
export const gammesCocheesParDefaut = (gammes: readonly GammeARecalculer[]): ReadonlySet<string> =>
  new Set(gammes.filter((g) => g.resume.aChanger).map((g) => g.profileId));

/** Ce qui part vers le serveur : pour chaque gamme cochée, la version en vigueur que la revue a montrée. */
export function gammesAEnvoyer(
  gammes: readonly GammeARecalculer[],
  cochees: ReadonlySet<string>,
): { profileId: string; versionId: string }[] {
  return gammes.filter((g) => cochees.has(g.profileId)).map((g) => ({ profileId: g.profileId, versionId: g.versionId }));
}

/** Coche ou décoche une gamme. */
export function basculerGamme(cochees: ReadonlySet<string>, profileId: string, valeur: boolean): ReadonlySet<string> {
  const suivant = new Set(cochees);
  if (valeur) suivant.add(profileId);
  else suivant.delete(profileId);
  return suivant;
}

/** « Tout cocher » ne coche que les gammes dont un prix changerait : les autres n'ont rien à gagner. */
export function cocherToutes(gammes: readonly GammeARecalculer[], valeur: boolean): ReadonlySet<string> {
  return valeur ? gammesCocheesParDefaut(gammes) : new Set();
}

/** Les cases ont-elles changé par rapport aux cases d'origine ? */
export function selectionModifiee(initiales: ReadonlySet<string>, courantes: ReadonlySet<string>): boolean {
  if (initiales.size !== courantes.size) return true;
  for (const id of initiales) if (!courantes.has(id)) return true;
  return false;
}

/** La date d'effet ne peut pas précéder celle de la version en vigueur d'une gamme envoyée : elle n'y entrerait jamais. */
export function erreurDeLaDate(
  validFrom: string,
  gammesEnvoyees: readonly { profileId: string }[],
  gammes: readonly GammeARecalculer[],
): string | null {
  const envoyees = new Set(gammesEnvoyees.map((g) => g.profileId));
  const plusRecente = gammes
    .filter((g) => envoyees.has(g.profileId))
    .map((g) => g.validFrom)
    .sort()
    .at(-1);
  return erreurDateDEffet(validFrom, plusRecente ?? null);
}

export interface TotauxRecalcul {
  readonly gammes: number;
  /** Prix de vente qui apparaissent (jamais calculés jusqu'ici). */
  readonly nouveaux: number;
  readonly changent: number;
  readonly perdus: number;
}

/** Ce que les gammes cochées changeraient, pour la barre du bas. */
export function totauxDe(gammes: readonly GammeARecalculer[], cochees: ReadonlySet<string>): TotauxRecalcul {
  const choisies = gammes.filter((g) => cochees.has(g.profileId));
  return {
    gammes: choisies.length,
    nouveaux: choisies.reduce((n, g) => n + g.resume.vente.nouveaux, 0),
    changent: choisies.reduce((n, g) => n + g.resume.vente.changent, 0),
    perdus: choisies.reduce((n, g) => n + g.resume.vente.perdus, 0),
  };
}

/** Une gamme dont les prix sont déjà à jour : rien à faire, on le dit sans l'effacer de la liste. */
export const estAJour = (g: GammeARecalculer): boolean => !g.resume.aChanger;

/**
 * Ce que le recalcul ferait à UNE gamme, en une phrase : « 20 prix de vente à calculer · 2 lignes sans prix de vente ».
 * Le prix de VENTE d'abord (celui que voit le client), puis le coût réel d'un équivalent quand il change à lui seul.
 */
export function resumeEnPhrase(r: ResumeGammeRecalcul): string {
  const parties: string[] = [];
  if (r.vente.nouveaux > 0) parties.push(accordFr(r.vente.nouveaux, 'prix de vente à calculer', 'prix de vente à calculer'));
  if (r.vente.changent > 0) parties.push(accordFr(r.vente.changent, 'prix de vente qui change', 'prix de vente qui changent'));
  if (r.vente.perdus > 0) parties.push(accordFr(r.vente.perdus, 'prix de vente qui ne se calcule plus', 'prix de vente qui ne se calculent plus'));
  const coutSeul = r.achat.nouveaux + r.achat.changent + r.achat.perdus;
  if (parties.length === 0 && coutSeul > 0) parties.push(accordFr(coutSeul, 'coût d’achat à mettre à jour', 'coûts d’achat à mettre à jour'));
  if (parties.length === 0) parties.push('prix à jour');
  const sansPrix = r.vente.nonCalcules - r.nbSansPrixTarif;
  if (sansPrix > 0) parties.push(`${lignesFr(sansPrix)} sans prix de vente (condition manquante)`);
  if (r.nbSansPrixTarif > 0) parties.push(`${lignesFr(r.nbSansPrixTarif)} sans prix du tarif`);
  return parties.join(' · ');
}

const EXEMPLES = (m: ManqueDeConditionPrix): string => (m.references.length > 0 ? ` Par exemple : ${m.references.join(', ')}.` : '');
const PORTEE = (m: ManqueDeConditionPrix): string => `${lignesFr(m.nbLignes)} dans ${accordFr(m.nbGammes, 'gamme', 'gammes')}`;

/** Le titre court d'un manque : l'écran le montre en gras, puis la phrase qui dit quoi saisir. */
export function titreManque(m: ManqueDeConditionPrix): string {
  if (m.chaine === 'ACHAT') return 'Coût d’achat réel inconnu';
  switch (m.raison) {
    case 'AUCUNE_CONDITION':
      return m.famille ? `Famille « ${m.famille} » : aucune condition` : 'Aucune condition applicable';
    case 'FAMILLE_INCONNUE':
      return 'Famille introuvable';
    case 'REFERENCE_AMBIGUE':
      return 'Référence classée différemment par plusieurs tarifs';
    case 'PRIX_TARIF_INUTILISABLE':
      return 'Prix du tarif inutilisable';
    case 'ACHAT_NUL':
    case 'MONTANT_INVRAISEMBLABLE':
      return 'Montant invraisemblable';
    case 'CONDITION_ILLISIBLE':
      return 'Condition illisible';
    case 'SANS_PRIX_EQUIVALENT':
    case 'SANS_PRIX_DU_TARIF':
      return 'Prix absent';
  }
}

/** La phrase qui dit QUOI saisir pour que ces lignes aient un prix. Les accords suivent le nombre de lignes. */
export function phraseManque(m: ManqueDeConditionPrix): string {
  const pl = (pluriel: string, singulier: string): string => (m.nbLignes > 1 ? pluriel : singulier);
  if (m.chaine === 'ACHAT') {
    const debut = `${PORTEE(m)} ${pl('ont', 'a')} un équivalent`;
    const fin = `${pl('leur', 'son')} coût d’achat réel n’est pas calculé.`;
    return m.raison === 'SANS_PRIX_EQUIVALENT'
      ? `${debut} sans prix : ${fin}${EXEMPLES(m)}`
      : `${debut} dont le fournisseur n’a pas de condition applicable : ${fin} Saisis ses conditions d’achat.${EXEMPLES(m)}`;
  }
  const sansPrix = `${PORTEE(m)} ${pl('n’ont', 'n’a')} pas de prix de vente`;
  switch (m.raison) {
    case 'AUCUNE_CONDITION':
      return m.famille
        ? `${sansPrix}. Saisis une condition pour cette famille, ou une exception par référence.${EXEMPLES(m)}`
        : `${sansPrix}. Saisis une condition (pour toute la marque, pour la famille ou pour la référence).${EXEMPLES(m)}`;
    case 'FAMILLE_INCONNUE':
      return (
        `${sansPrix} : ${pl('les références sont absentes du tarif (ou sans famille)', 'la référence est absente du tarif (ou sans famille)')} ` +
        `et la marque a des conditions par famille. Importe le tarif qui ${pl('les porte', 'la porte')}, ou pose une exception ` +
        `${pl('pour chaque référence', 'pour cette référence')}.${EXEMPLES(m)}`
      );
    case 'REFERENCE_AMBIGUE':
      return (
        `${sansPrix} : ${pl('les références sont classées', 'la référence est classée')} différemment (ou à un autre prix) par ` +
        `plusieurs tarifs de la marque. Pose une exception ${pl('pour chaque référence', 'pour cette référence')}.${EXEMPLES(m)}`
      );
    case 'PRIX_TARIF_INUTILISABLE':
      return `${PORTEE(m)} ${pl('ont', 'a')} un prix du tarif à 0 € ou hors limites : aucun prix de vente n’est calculé.${EXEMPLES(m)}`;
    case 'ACHAT_NUL':
    case 'MONTANT_INVRAISEMBLABLE':
      return `${PORTEE(m)} ${pl('donneraient', 'donnerait')} un montant nul ou invraisemblable : vérifie la remise et le coefficient.${EXEMPLES(m)}`;
    case 'CONDITION_ILLISIBLE':
      return `${PORTEE(m)} ${pl('reposent', 'repose')} sur une condition illisible : préviens l’administrateur.${EXEMPLES(m)}`;
    case 'SANS_PRIX_EQUIVALENT':
    case 'SANS_PRIX_DU_TARIF':
      return `${PORTEE(m)} ${pl('n’ont', 'n’a')} pas de prix.${EXEMPLES(m)}`;
  }
}

/** Une gamme sans changement n'est pas un refus : le serveur la signale, l'écran la range à part. */
export const estRienAChanger = (g: Pick<BilanGammeRecalculPrix, 'refus'>): boolean => g.refus?.code === 'AUCUN_CHANGEMENT';

/** La phrase qui annonce le résultat de la création des brouillons. */
export function messageBilan(bilan: Pick<BilanRecalculPrix, 'brouillons' | 'refus'>): string {
  if (bilan.brouillons === 0) {
    return 'Aucun brouillon n’a été créé : voir, gamme par gamme, ce qui a été refusé.';
  }
  return (
    `${accordFr(bilan.brouillons, 'brouillon créé', 'brouillons créés')}. ` +
    'Rien ne change pour les machines tant qu’ils ne sont pas publiés : les nouveaux prix se calculent et se figent à la publication.' +
    (bilan.refus > 0 ? ` ${accordFr(bilan.refus, 'gamme refusée', 'gammes refusées')}, détail ci-dessous.` : '')
  );
}

/** Le titre d'un refus de gamme, en français (la phrase de détail vient du serveur). */
export const TITRE_REFUS_RECALCUL: Readonly<Record<CodeRefusGamme, string>> = {
  INTROUVABLE: 'Gamme introuvable',
  ARCHIVEE: 'Gamme archivée',
  SANS_VERSION: 'Aucune version en vigueur',
  A_CHANGE: 'La gamme a changé depuis la revue',
  BROUILLON_OUVERT: 'Brouillon déjà ouvert',
  VERSION_A_VENIR: 'Version à venir',
  DATE_ANTERIEURE: 'Date d’effet trop ancienne',
  AUCUN_CHANGEMENT: 'Rien à changer',
  CONTENU_INVALIDE: 'Contenu refusé',
  FOURNISSEUR_ARCHIVE: 'Fournisseur archivé',
  CONFLIT: 'Création simultanée',
  ERREUR: 'Erreur imprévue',
};
